<template>
    <raw-date-picker
        :model-value="internalValue"
        :mode="mode"
        :locale="locale"
        :min-date="minDate"
        :max-date="maxDate"
        :start-date="startDate"
        :selection-flow="selectionFlow"
        :disabled="disabled"
        :clearable="clearable"
        :placeholder="placeholder"
        :preset-dates="translatedPresets"
        :multi-calendars="multiCalendars"
        :time-slot-interval-minutes="timeSlotIntervalMinutes"
        :time-slot-grid="timeSlotGrid"
        :time-format="timeFormat"
        :date-format="dateFormat"
        :show-action-buttons="showActionButtons"
        :initially-open="initiallyOpen"
        :compact="compact"
        :start-label="startLabel"
        :end-label="endLabel"
        :previous-day-label="previousDayLabel"
        :next-day-label="nextDayLabel"
        :submit-label="submitLabel"
        :cancel-label="cancelLabel"
        :clear-label="clearLabel"
        @update:model-value="onUpdate"
    >
        <template #trigger v-if="$slots.trigger">
            <slot name="trigger" />
        </template>
    </raw-date-picker>
</template>

<script setup lang="ts">
    /**
     * The app-facing date picker. Wraps the bare {@link RawDatePicker} with the
     * app's defaults and conventions, such as:
     *   - using ISO date strings in the model for interoperability with the rest of the app
     *   - translating built-in UI labels and preset date labels
     *   - injecting a locale-aware trigger date format
     *   - defaulting to the account timezone for date parsing and formatting
     *
     * Reach for this component in app code; RawDatePicker is the dependency-free
     * primitive it builds on and is not meant to be used directly.
     */
    import { computed } from 'vue';
    import ActivixDate from '@/value-objects/ActivixDate.js';
    import { getDateFnsLocale, getCurrentTimezone } from '@/value-objects/dateEnvironment.ts';
    import RawDatePicker from '@/components/activix-ui/ActivixDatePicker/RawDatePicker.vue';
    import { DEFAULT_TIME_SLOT_INTERVAL_MINUTES } from '@/components/activix-ui/ActivixDatePicker/constants.ts';
    import type {
        DatePickerValue,
        RawDatePickerProps,
    } from '@/components/activix-ui/ActivixDatePicker/RawDatePicker.vue';
    import { useI18n } from '@/hooks/useI18n.ts';

    /**
     * App-facing preset definition. Labels resolve in priority order:
     *   1. `label` — plain display text, used verbatim (already translated, or
     *      a dynamic string the caller built). Wins if present.
     *   2. `labelKey` (+ optional `labelParams`) — an i18n key the wrapper
     *      translates, with interpolation params for dynamic labels such as
     *      `datepicker.olderThan` with `{ months }`.
     * Provide one of `label` or `labelKey`. If both are set, `label` wins.
     */
    export interface DatePreset {
        label?: string;
        labelKey?: string;
        labelParams?: Record<string, unknown>;
        value: Date | [Date, Date];
    }

    type SharedProps = Pick<
        RawDatePickerProps,
        | 'mode'
        | 'minDate'
        | 'maxDate'
        | 'startDate'
        | 'selectionFlow'
        | 'disabled'
        | 'clearable'
        | 'placeholder'
        | 'multiCalendars'
        | 'timeSlotIntervalMinutes'
        | 'timeSlotGrid'
        | 'timeFormat'
        | 'showActionButtons'
        | 'initiallyOpen'
    >;

    export interface ActivixDatePickerProps extends SharedProps {
        modelValue?: string | [string, string] | null;
        presetDates?: DatePreset[];
        // Collapse the trigger to just the calendar icon. Defaults to false; the
        // caller opts in explicitly (there is no automatic viewport-based collapse).
        compact?: boolean;
    }

    const props = withDefaults(defineProps<ActivixDatePickerProps>(), {
        modelValue: null,
        mode: 'date',
        minDate: undefined,
        maxDate: undefined,
        startDate: undefined,
        selectionFlow: undefined,
        disabled: false,
        clearable: false,
        placeholder: undefined,
        presetDates: undefined,
        multiCalendars: undefined,
        timeSlotIntervalMinutes: DEFAULT_TIME_SLOT_INTERVAL_MINUTES,
        timeSlotGrid: false,
        timeFormat: 'locale',
        showActionButtons: true,
        initiallyOpen: false,
        compact: false,
    });

    const emit = defineEmits<{
        'update:modelValue': [value: string | [string, string] | null];
    }>();

    const { t } = useI18n();

    // `PP` renders the date in the active locale's own order (date-fns localized
    // format), so French reads "10 juin 2024" rather than the English month-first
    // order. The locale itself is injected separately.
    const dateFormat = 'PP';

    const locale = computed(() => getDateFnsLocale());
    const startLabel = computed(() => t('datepicker.start'));
    const endLabel = computed(() => t('datepicker.end'));
    const previousDayLabel = computed(() => t('datepicker.previousDay'));
    const nextDayLabel = computed(() => t('datepicker.nextDay'));
    const submitLabel = computed(() => t('datepicker.submit'));
    const cancelLabel = computed(() => t('datepicker.cancel'));
    const clearLabel = computed(() => t('datepicker.clear'));
    const placeholder = computed(() => {
        if (props.placeholder) {
            return props.placeholder;
        }

        return t(props.mode === 'range' ? 'datepicker.selectDateRange' : 'datepicker.selectDate');
    });

    const internalValue = computed<DatePickerValue>(() => {
        if (!props.modelValue) return null;

        if (Array.isArray(props.modelValue)) {
            const dates = props.modelValue.map((iso) => new ActivixDate(iso).toDate());

            return dates.every((date): date is Date => date !== null) ? dates : null;
        }

        return new ActivixDate(props.modelValue).toDate();
    });

    const translatedPresets = computed(() => {
        if (!props.presetDates) return undefined;

        // Resolve each preset down to the primitive's plain-text label. A
        // verbatim `label` wins; otherwise translate `labelKey` with its params.
        return props.presetDates.map((preset) => ({
            value: preset.value,
            label: preset.label ?? (preset.labelKey ? t(preset.labelKey, preset.labelParams) : ''),
        }));
    });

    function onUpdate(value: Date | Date[] | null) {
        if (!value) {
            emit('update:modelValue', null);
            return;
        }

        if (Array.isArray(value)) {
            const start = value[0];
            if (!(start instanceof Date)) {
                emit('update:modelValue', null);
                return;
            }

            // There can be a situation when user picks only a start date, no end date. We need
            // to provide the end date too - in this case, we default to using the same date, so
            // the picker will effectively select a "single day".
            const end = value[1] instanceof Date ? value[1] : start;
            emit('update:modelValue', [toIsoDate(start), toIsoDate(end)]);
            return;
        }

        emit('update:modelValue', toIsoDate(value));
    }

    /**
     * Converts a native Date from the inner picker into the wire-format string
     * the app stores and reads. The format follows the {@link ActivixDate}
     * convention used across the app, expressed in the account timezone:
     *   - datetime mode: offset form `yyyy-MM-dd'T'HH:mm:ssXXX` (e.g. `-04:00`),
     *     never browser UTC (`Z`), so the round-trip is timezone-stable.
     *   - date and range modes: a bare calendar day `yyyy-MM-dd` with no
     *     timezone, matching how the rest of the app treats date-only values.
     */
    function toIsoDate(date: Date): string {
        const timeZone = getCurrentTimezone();

        if (props.mode === 'datetime') {
            return new ActivixDate(date, 'dateTime', timeZone).toDateTimeString();
        }

        return new ActivixDate(date, 'date', timeZone).toDateString();
    }
</script>
